import React from 'react';
import { Card, Row, Col, Empty, Tag, Statistic } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PercentageOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  AimOutlined,
  FileTextOutlined,
  AuditOutlined
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';

/**
 * 机审结果统计展示组件
 * 支持最终数据结构：中文键名 + 规则统计明细数组
 */
function MachineAuditStatsDisplay({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Empty
        description="暂无统计数据"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // 解析百分比字符串为数值
  const parsePercentage = (percentStr) => {
    if (!percentStr || typeof percentStr !== 'string') return 0;
    return parseFloat(percentStr.replace('%', '')) || 0;
  };

  // 根据准确率获取颜色
  const getColorByRate = (rate) => {
    if (rate >= 90) return '#52c41a';
    if (rate >= 75) return '#1890FF';
    if (rate >= 60) return '#faad14';
    return '#ff4d4f';
  };

  // 指标配置映射
  const metricConfig = {
    '免审率': { icon: <SafetyOutlined />, color: '#52c41a' },
    '单据无风险召回率': { icon: <CheckCircleOutlined />, color: '#13c2c2' },
    '单据无风险精确率': { icon: <AimOutlined />, color: '#1890FF' },
    '单据有风险召回率': { icon: <CloseCircleOutlined />, color: '#fa8c16' },
    '单据有风险精确率': { icon: <AimOutlined />, color: '#f5222d' },
    '规则平均准确率': { icon: <ThunderboltOutlined />, color: '#722ed1' },
    '规则平均精确率': { icon: <ThunderboltOutlined />, color: '#eb2f96' },
  };

  // 获取核心百分比指标（排除字段要素抽取平均准确率）
  const coreMetrics = Object.entries(data)
    .filter(([key, value]) => typeof value === 'string' && value.includes('%') && key !== '字段要素抽取平均准确率')
    .map(([name, value]) => ({
      name: name.replace('单据', '').replace('平均', ''),
      fullName: name,
      value: parsePercentage(value),
      displayValue: value,
      color: metricConfig[name]?.color || '#1890FF'
    }));

  // 获取规则统计明细
  const ruleDetails = data['规则统计明细'] || [];
  const hasRuleData = ruleDetails.length > 0;

  // 构建规则准确率图表数据
  const ruleChartData = ruleDetails.map(rule => ({
    name: rule.rule_name || rule.rule_id,
    准确率: parsePercentage(rule.accuracy_rate),
    准确率显示: rule.accuracy_rate || '-'
  }));

  // 单据统计数据
  const docStats = {
    total: data['单据总数'],
    machineNoRisk: data['机审无风险单据数'],
    machineHasRisk: data['机审有风险单据数'],
    humanNoRisk: data['人工确认无风险单据数'],
    humanHasRisk: data['人工确认有风险单据数'],
    matchNoRisk: data['机审人工一致无风险数'],
    matchHasRisk: data['机审人工一致有风险数']
  };
  const hasDocStats = docStats.total !== undefined;

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#fff',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #f0f0f0'
        }}>
          <p style={{ margin: 0, fontWeight: 600, marginBottom: '8px' }}>{payload[0]?.payload?.fullName || payload[0]?.payload?.name}</p>
          {payload.map((item, index) => (
            <p key={index} style={{ margin: '4px 0', color: item.color || '#333' }}>
              {item.name}: <span style={{ fontWeight: 600 }}>{item.value.toFixed(2)}%</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // 渲染小卡片
  const renderMiniCard = (title, value, color, icon) => (
    <div
      className="flex items-center gap-3 p-4 rounded-xl hover:shadow-md transition-all duration-300"
      style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: color }}
      >
        {React.cloneElement(icon, { style: { fontSize: '18px', color: '#fff' } })}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 truncate">{title}</div>
        <div className="text-lg font-bold" style={{ color }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div className="machine-audit-stats-display">
      {/* 核心指标区域 */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <PercentageOutlined style={{ color: '#1890FF', fontSize: '18px' }} />
            <span className="font-semibold">核心指标</span>
          </div>
        }
        className="mb-6"
        style={{ borderRadius: '12px' }}
      >
        {/* 图表展示 */}
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={coreMetrics}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11, fill: '#666' }}
                angle={-25}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis 
                domain={[0, 100]} 
                tick={{ fontSize: 12, fill: '#666' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={50}>
                {coreMetrics.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList 
                  dataKey="displayValue" 
                  position="top" 
                  style={{ fontSize: 11, fontWeight: 600, fill: '#333' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 小卡片展示 */}
        <Row gutter={[12, 12]} className="mt-4">
          {coreMetrics.map((item) => {
            const config = metricConfig[item.fullName] || { icon: <PercentageOutlined />, color: '#1890FF' };
            return (
              <Col xs={12} sm={8} lg={4} key={item.fullName}>
                {renderMiniCard(item.fullName, item.displayValue, config.color, config.icon)}
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* 单据统计卡片 */}
      {hasDocStats && (
        <Card
          title={
            <div className="flex items-center gap-2">
              <FileTextOutlined style={{ color: '#1890FF', fontSize: '18px' }} />
              <span className="font-semibold">单据统计</span>
            </div>
          }
          className="mb-6"
          style={{ borderRadius: '12px' }}
        >
          <Row gutter={[24, 24]}>
            {/* 总数 */}
            <Col xs={24} sm={8} lg={4}>
              <Statistic
                title={<span className="text-gray-600">单据总数</span>}
                value={docStats.total}
                valueStyle={{ color: '#1890FF', fontWeight: 700 }}
                prefix={<FileTextOutlined />}
              />
            </Col>
            {/* 机审统计 */}
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#f6ffed' }}>
                <div className="text-xs text-gray-500 mb-2">机审无风险</div>
                <div className="text-2xl font-bold text-green-600">{docStats.machineNoRisk ?? '-'}</div>
              </div>
            </Col>
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#fff2f0' }}>
                <div className="text-xs text-gray-500 mb-2">机审有风险</div>
                <div className="text-2xl font-bold text-red-500">{docStats.machineHasRisk ?? '-'}</div>
              </div>
            </Col>
            {/* 人工确认统计 */}
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#e6f7ff' }}>
                <div className="text-xs text-gray-500 mb-2">人工确认无风险</div>
                <div className="text-2xl font-bold text-blue-500">{docStats.humanNoRisk ?? '-'}</div>
              </div>
            </Col>
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#fff7e6' }}>
                <div className="text-xs text-gray-500 mb-2">人工确认有风险</div>
                <div className="text-2xl font-bold text-orange-500">{docStats.humanHasRisk ?? '-'}</div>
              </div>
            </Col>
            {/* 一致性统计 */}
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#f9f0ff' }}>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <AuditOutlined /> 一致无风险
                </div>
                <div className="text-2xl font-bold text-purple-600">{docStats.matchNoRisk ?? '-'}</div>
              </div>
            </Col>
            <Col xs={12} sm={8} lg={5}>
              <div className="p-4 rounded-lg" style={{ background: '#fcf4dc' }}>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <AuditOutlined /> 一致有风险
                </div>
                <div className="text-2xl font-bold text-yellow-600">{docStats.matchHasRisk ?? '-'}</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* 规则准确率详情区域 */}
      {hasRuleData && (
        <Card
          title={
            <div className="flex items-center gap-2">
              <ThunderboltOutlined style={{ color: '#722ed1', fontSize: '18px' }} />
              <span className="font-semibold">规则准确率详情</span>
              <Tag color="purple" style={{ marginLeft: '8px' }}>
                共 {ruleDetails.length} 条规则
              </Tag>
            </div>
          }
          style={{ borderRadius: '12px' }}
        >
          {/* 横向柱状图 - 只展示准确率 */}
          <div style={{ height: Math.max(180, ruleDetails.length * 50) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ruleChartData}
                layout="vertical"
                margin={{ top: 10, right: 80, left: 60, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis 
                  type="number" 
                  domain={[0, 100]}
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 13, fill: '#333', fontWeight: 500 }}
                  width={70}
                />
                <Tooltip
                  formatter={(value) => [`${value.toFixed(2)}%`, '准确率']}
                  contentStyle={{
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #f0f0f0'
                  }}
                />
                <Bar 
                  dataKey="准确率" 
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                >
                  {ruleChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColorByRate(entry.准确率)} />
                  ))}
                  <LabelList 
                    dataKey="准确率显示" 
                    position="right" 
                    style={{ fontSize: 12, fontWeight: 600, fill: '#333' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 规则详情小卡片 */}
          <Row gutter={[12, 12]} className="mt-4">
            {ruleDetails.map((rule, index) => (
              <Col xs={24} sm={12} key={rule.rule_id || index}>
                <div 
                  className="p-4 rounded-xl"
                  style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
                >
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <ThunderboltOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
                      <span className="font-semibold text-gray-700">{rule.rule_name || rule.rule_id}</span>
                    </div>
                    <Tag color={getColorByRate(parsePercentage(rule.accuracy_rate)) === '#52c41a' ? 'success' : 
                               getColorByRate(parsePercentage(rule.accuracy_rate)) === '#1890FF' ? 'processing' :
                               getColorByRate(parsePercentage(rule.accuracy_rate)) === '#faad14' ? 'warning' : 'error'}>
                      准确率: {rule.accuracy_rate}
                    </Tag>
                  </div>
                  <Row gutter={[16, 8]}>
                    <Col span={8}>
                      <div className="text-xs text-gray-500">准确/总数</div>
                      <div className="font-semibold">{rule.accurate_count}/{rule.total_count}</div>
                    </Col>
                    <Col span={8}>
                      <div className="text-xs text-gray-500">无风险精确率</div>
                      <div className="font-semibold text-green-600">{rule.no_risk_precision || '-'}</div>
                    </Col>
                    <Col span={8}>
                      <div className="text-xs text-gray-500">有风险精确率</div>
                      <div className="font-semibold text-red-500">{rule.has_risk_precision || '-'}</div>
                    </Col>
                  </Row>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );
}

export default MachineAuditStatsDisplay;
